import { useState } from 'react';
import {
  CareBenefitUsageToInvoiceLineItemCreateInput,
  useCreateCareBenefitUsageToInvoiceLineItemMutation,
} from 'shared/types/graphql';
import isEqual from 'lodash-es/isEqual';
import { UsePetBenefitCareBenefit } from './usePetBenefits';

interface IMarkBenefitsAdjustmentInput {
  adjustedAmount: number;
  adjustedValue: number;
  benefitId: string;
  clinicPetId: string;
  serviceIds: string[];
  lineItemIds: string[];
  reason?: string;
}

interface IUseMarkPetBenefitUsageOutput {
  markAdjustment: ({
    adjustedAmount,
    adjustedValue,
    benefitId,
    serviceIds,
    lineItemIds,
    reason,
  }: IMarkBenefitsAdjustmentInput) => Promise<void>;
  adjustments: IAdjustment[];
  setAdjustments: (adjustments: IAdjustment[]) => void;
  addBenefitAdjustments: (adjustments: IAdjustment[], clearCurrentAdjustments?: boolean) => void;
  addLineItemToAdjustment: (benefitId: string, newLineItemId: string, addedValue: number) => void;
  removeLineItemFromBenefitAdjustments: (benefits: UsePetBenefitCareBenefit[], lineItemId: string) => void;
  replaceBenefitForAdjustments: (oldBenefitId: string, newBenefitId: string, lineItemId: string) => void;
  creatingUsages: boolean;
}

export interface IAdjustment {
  benefitId: string;
  adjustedAmount: string;
  adjustedValue: string;
  clinicPetId: string;
  lineItemIds: string[];
  reason?: string;
  exhaustibleBenefit?: boolean;
}

/**
 * A simple hook to help mark a pet benefit as used and track/update state
 * @param clinicId - The id of the clinic providing the benefit
 * @returns
 */
export const useMarkPetBenefitUsage = (clinicId: string): IUseMarkPetBenefitUsageOutput => {
  const [adjustments, setAdjustments] = useState<IAdjustment[]>([]);
  const [createPetBenefitUsageToInvoiceLineItem, { loading: creatingUsages }] =
    useCreateCareBenefitUsageToInvoiceLineItemMutation();

  /**
   * Adds an array of new benefit adjustments to the array of pending adjustments. If adjustments already exist for a provided benefit and line item ID, the new one will replace the existing one.
   * @param addedAdjustments - The IAdjustments to add to the pending adjustments.
   * @param clearCurrentAdjustments - Flag indicating whether the existing adjustments should be cleared before adding the new ones
   * @returns
   */
  const addBenefitAdjustments = (addedAdjustments: IAdjustment[], clearCurrentAdjustments?: boolean): void => {
    setAdjustments((currentAdjustments) => {
      const newAdjustments: IAdjustment[] = clearCurrentAdjustments ? [] : [...currentAdjustments];

      // add new adjustments to existing adjustments
      addedAdjustments.forEach((addedAdjustment) => {
        if (!addedAdjustment.benefitId) return;

        // look for existing adjustment for same benefit and line items and remove if found so it can be replaced with new value
        const existingAdjustmentIndex = newAdjustments.findIndex(
          (benefitAdjustment) =>
            isEqual(benefitAdjustment.lineItemIds.sort(), addedAdjustment.lineItemIds.sort()) &&
            benefitAdjustment.benefitId === addedAdjustment.benefitId,
        );

        if (existingAdjustmentIndex !== -1) {
          newAdjustments.splice(existingAdjustmentIndex, 1);
        }

        newAdjustments.push(addedAdjustment);
      });

      return newAdjustments;
    });
  };

  /**
   * Adds a line item to an existing adjustment. If an adjustment doesn't exist for the provided benefit, nothing happens.
   * @param benefitId - The ID of the benefit you are adding a line item to.
   * @param newLineItemId - The ID of the new lineItem you are associating with the existing adjustment.
   * @param addedValue - The adjusted value from the new line item that will get added to the total adjustment value.
   * @returns
   */
  const addLineItemToAdjustment = (benefitId: string, newLineItemId: string, addedValue: number): void => {
    setAdjustments((currentAdjustments) => {
      const newAdjustments: IAdjustment[] = [...currentAdjustments];

      // auto-apply is greedy, so add to first match and assume there aren't multiple adjustments for same benefit
      // skip if line item is already in this adjustment so that we don't add it twice
      const existingAdjustmentIndex = newAdjustments.findIndex(
        (adjustment) => adjustment.benefitId === benefitId && !adjustment.lineItemIds.includes(newLineItemId),
      );

      // if existing adjustment exists, add lineItemId and added value to it
      if (existingAdjustmentIndex !== -1) {
        newAdjustments[existingAdjustmentIndex].lineItemIds.push(newLineItemId);
        newAdjustments[existingAdjustmentIndex].adjustedValue += addedValue;
      }

      return newAdjustments;
    });
  };

  /**
   * Removes a line item from any pending adjustment for the provided benefits. If it is the only line item associate to the adjustment, the adjustment is removed.
   * @param benefits - The benefits associated to the adjustments you are removing the line item from.
   * @param lineItemId - The ID of the lineItem associated with the existing adjustment.
   * @returns
   */
  const removeLineItemFromBenefitAdjustments = (benefits: UsePetBenefitCareBenefit[], lineItemId: string): void => {
    setAdjustments((currentAdjustments) => {
      const newAdjustments: IAdjustment[] = [...currentAdjustments];

      const benefitIds = benefits.map((benefit) => benefit.id);

      for (const adjustment of newAdjustments) {
        if (adjustment.lineItemIds.includes(lineItemId) && benefitIds.includes(adjustment.benefitId)) {
          const lineItemIndex = adjustment.lineItemIds.indexOf(lineItemId);
          adjustment.lineItemIds.splice(lineItemIndex, 1);
        }
      }

      // remove any adjustments that have all line items removed
      return newAdjustments.filter((adjustment) => adjustment.lineItemIds.length);
    });
  };
  /**
   * When a lineItem has multiple benefits that can be applied to it, when the user selects a new benefit from the drop down menu this function is used to replace the old benefit with the new one in the adjustments.
   * @param oldBenefitId - Benefit ID of the benefit that is being replaced.
   * @param newBenefitId - Benefit ID of the benefit that is replacing the old one.
   * @param lineItemId - The ID of the lineItem that is being adjusted.
   * @returns
   */
  const replaceBenefitForAdjustments = (oldBenefitId: string, newBenefitId: string, lineItemId: string): void => {
    setAdjustments((currentAdjustments) => {
      const newAdjustments: IAdjustment[] = [...currentAdjustments];

      for (let i = 0; i < newAdjustments.length; i++) {
        const adjustment = newAdjustments[i];

        if (adjustment.lineItemIds.includes(lineItemId) && adjustment.benefitId === oldBenefitId) {
          newAdjustments[i].benefitId = newBenefitId;
        }
      }
      return newAdjustments;
    });
  };

  const markAdjustment = async ({
    adjustedAmount,
    adjustedValue,
    clinicPetId,
    benefitId,
    serviceIds,
    lineItemIds,
    reason,
  }: IMarkBenefitsAdjustmentInput): Promise<void> => {
    const data: CareBenefitUsageToInvoiceLineItemCreateInput = {
      adjustedAmount: adjustedValue,
      careBenefitUsage: {
        create: {
          usageClinic: { connect: { id: clinicId } },
          usageClinicPet: { connect: { id: clinicPetId } },
          benefit: { connect: { id: benefitId } },
          usedAt: new Date(),
          quantityUsed: adjustedAmount,
          reason: reason,
        },
      },
    };

    if (serviceIds.length && serviceIds[0]) {
      data.draftService = {
        connect: {
          id: serviceIds[0],
        },
      };
    }

    if (lineItemIds.length && lineItemIds[0]) {
      data.invoiceLineItem = {
        connect: {
          id: lineItemIds[0],
        },
      };
    }

    // create usage and connect to first line item
    const { data: usageToLineItem } = await createPetBenefitUsageToInvoiceLineItem({
      variables: {
        data: { ...data },
      },
    });

    // connect created usage to remaining line items if there's more than one. Should only happen with real invoices
    // should have one entry in serviceIds per line item
    if (lineItemIds.length > 1) {
      for (let i = 1; i < lineItemIds.length; i++) {
        const data: CareBenefitUsageToInvoiceLineItemCreateInput = {
          adjustedAmount: adjustedValue,
          careBenefitUsage: {
            connect: {
              id: usageToLineItem?.createOneCareBenefitUsageToInvoiceLineItem.id,
            },
          },
        };

        if (serviceIds.length && serviceIds[i]) {
          data.draftService = {
            connect: {
              id: serviceIds[i],
            },
          };
        }

        if (lineItemIds.length && lineItemIds[i]) {
          data.invoiceLineItem = {
            connect: {
              id: lineItemIds[i],
            },
          };
        }

        await createPetBenefitUsageToInvoiceLineItem({
          variables: {
            data: { ...data },
          },
        });
      }
    }
  };

  return {
    markAdjustment,
    adjustments,
    setAdjustments,
    addBenefitAdjustments,
    addLineItemToAdjustment,
    removeLineItemFromBenefitAdjustments,
    replaceBenefitForAdjustments,
    creatingUsages,
  };
};
